import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface ClientUser {
  /** Client user UUID. */
  id: string
  /** Email address used to log in. */
  email: string
  /** Display name. May be null for SSO users until first sync. */
  fullName: string | null
  /** Tenant slug the user belongs to. */
  tenantSlug: string
  /** Granted permissions such as `conversations:view`, `conversations:create`. */
  permissions: string[]
}

interface ClientAuthState {
  user: ClientUser | null
  csrfToken: string | null
  accessToken: string | null
  refreshToken: string | null
  expiresAt: number | null
  hasHydrated: boolean
  setSession: (session: {
    user: ClientUser
    csrfToken: string
    accessToken?: string | null
    refreshToken?: string | null
    expiresAt?: number | null
  }) => void
  setTokens: (tokens: {
    accessToken: string
    refreshToken?: string | null
    expiresAt?: number | null
  }) => void
  clearSession: () => void
  hasPermission: (permission: string) => boolean
  setHasHydrated: (hydrated: boolean) => void
}

export const useClientAuthStore = create<ClientAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      csrfToken: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      hasHydrated: false,
      setSession: (session) =>
        set({
          user: session.user,
          csrfToken: session.csrfToken,
          accessToken: session.accessToken ?? null,
          refreshToken: session.refreshToken ?? null,
          expiresAt: session.expiresAt ?? null,
        }),
      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken ?? get().refreshToken,
          expiresAt: tokens.expiresAt ?? get().expiresAt,
        }),
      clearSession: () =>
        set({
          user: null,
          csrfToken: null,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
        }),
      hasPermission: (permission) => {
        const user = get().user
        if (!user) return false
        return user.permissions.includes(permission) || user.permissions.includes('*')
      },
      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'kc3-client-auth-storage',
      partialize: (state) => ({
        user: state.user,
        // SW-15: the CSRF token is memory-only (the client portal is Bearer-only and CSRF-exempt).
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
      }),
      // Entries written before SW-15 may contain a csrfToken: never restore it.
      merge: (persisted, current) => ({ ...(current as object), ...(persisted as object), csrfToken: null }) as ClientAuthState,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)

/** Resolves once the persisted client auth state has been rehydrated from storage. */
export function waitForClientAuthHydration(): Promise<void> {
  if (useClientAuthStore.getState().hasHydrated) return Promise.resolve()
  return new Promise((resolve) => {
    const unsubscribe = useClientAuthStore.subscribe((state) => {
      if (state.hasHydrated) {
        unsubscribe()
        resolve()
      }
    })
  })
}
